/**
 * TenantCompaniesTab.tsx — F7a T7.5.
 *
 * Zakładka "Firmy" na AdminTenantDetail. Pokazuje companies z tenant DB
 * + liczba użytkowników per company. Search toolbar (debounce 300ms min 2).
 *
 * Data: GET /api/admin/tenants/:id/companies?search=.
 * Error handling: `dbUnavailable:true` → info box. HTTP error → alert + retry.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';

import { Badge, type BadgeColor } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Skeleton } from '@/components/Skeleton';
import { Table, type TableColumn } from '@/components/Table';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { extractErrorCode, extractFieldErrors } from '@/lib/errors';

interface CompanyRow {
  id: string;
  legalName: string;
  shortName: string | null;
  taxId: string | null;
  isPrimary: boolean;
  isActive: boolean;
  email: string | null;
  phone: string | null;
  createdAt: string;
  gcrecord: string | null;
  usersCount: number;
}

interface CompaniesResponse {
  tenant: { id: string; slug: string; code: string };
  companies: CompanyRow[];
  total: number;
  limit: number;
  offset: number;
  dbUnavailable: boolean;
  dbUnavailableReason?: string;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return iso;
  }
}

export interface TenantCompaniesTabProps {
  tenantId: string;
}

export function TenantCompaniesTab({ tenantId }: TenantCompaniesTabProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');

  // Dodawanie / edycja firmy tenanta (wspólny modal ITE). editingId=null → tworzenie.
  const [addOpen, setAddOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addForm, setAddForm] = useState({ legalName: '', shortName: '', taxId: '' });
  const [taxIdError, setTaxIdError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<CompanyRow | null>(null);

  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed.length > 0 && trimmed.length < 2) {
      setSearchDebounced('');
      return;
    }
    const timeout = setTimeout(() => setSearchDebounced(trimmed), 300);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const query = useQuery({
    queryKey: ['admin', 'tenants', tenantId, 'companies', searchDebounced],
    queryFn: async (): Promise<CompaniesResponse> => {
      const params = new URLSearchParams();
      if (searchDebounced) params.set('search', searchDebounced);
      const suffix = params.toString() ? `?${params.toString()}` : '';
      const res = await api.get<CompaniesResponse>(`/admin/tenants/${tenantId}/companies${suffix}`);
      return res.data;
    },
  });

  const resetAddForm = () => {
    setAddForm({ legalName: '', shortName: '', taxId: '' });
    setTaxIdError(null);
  };

  const openCreate = () => {
    resetAddForm();
    setEditingId(null);
    setAddOpen(true);
  };

  const openEdit = (row: CompanyRow) => {
    setAddForm({
      legalName: row.legalName,
      shortName: row.shortName ?? '',
      taxId: row.taxId ?? '',
    });
    setTaxIdError(null);
    setEditingId(row.id);
    setAddOpen(true);
  };

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'tenants', tenantId, 'companies'] });

  // Zapis: PATCH gdy editingId, inaczej POST (tworzenie).
  const saveMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { legalName: addForm.legalName.trim() };
      body.shortName = addForm.shortName.trim() || null;
      body.taxId = addForm.taxId.trim() || null;
      if (editingId) {
        await api.patch(`/admin/tenants/${tenantId}/companies/${editingId}`, body);
      } else {
        await api.post(`/admin/tenants/${tenantId}/companies`, body);
      }
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: editingId
          ? t('admin.tenants.detail.companies.add.updated')
          : t('admin.tenants.detail.companies.add.created'),
      });
      invalidate();
      setAddOpen(false);
      resetAddForm();
      setEditingId(null);
    },
    onError: (err) => {
      // Serwer odrzucił NIP (VALIDATION_ERROR z details.taxId) → pokaż pod polem.
      if (extractErrorCode(err) === 'VALIDATION_ERROR') {
        const fields = extractFieldErrors(err);
        if (fields.taxId?.length) {
          setTaxIdError(t('admin.tenants.detail.companies.add.taxIdInvalid'));
          return;
        }
      }
      toast.show({ variant: 'error', title: t('admin.tenants.detail.companies.add.error') });
    },
  });

  // Miękkie usuwanie firmy (blokady PRIMARY_NOT_DELETABLE / LAST_COMPANY po stronie serwera).
  const deleteMutation = useMutation({
    mutationFn: async (companyId: string) => {
      await api.delete(`/admin/tenants/${tenantId}/companies/${companyId}`);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('admin.tenants.detail.companies.remove.done') });
      invalidate();
      setPendingDelete(null);
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      const key =
        code === 'PRIMARY_NOT_DELETABLE'
          ? 'admin.tenants.detail.companies.remove.primaryBlocked'
          : code === 'LAST_COMPANY'
            ? 'admin.tenants.detail.companies.remove.lastBlocked'
            : 'admin.tenants.detail.companies.remove.error';
      toast.show({ variant: 'error', title: t(key) });
      setPendingDelete(null);
    },
  });

  const statusColor = (row: CompanyRow): BadgeColor => {
    if (row.gcrecord) return 'red';
    if (!row.isActive) return 'gray';
    return 'green';
  };

  const statusLabel = (row: CompanyRow): string => {
    if (row.gcrecord) return t('admin.tenants.detail.companies.status.deleted');
    if (!row.isActive) return t('admin.tenants.detail.companies.status.inactive');
    return t('admin.tenants.detail.companies.status.active');
  };

  const columns = useMemo<TableColumn<CompanyRow>[]>(
    () => [
      {
        key: 'legalName',
        header: t('admin.tenants.detail.companies.columns.name'),
        accessor: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-text-primary">{row.legalName}</span>
            {row.shortName && (
              <span className="text-xs text-text-tertiary font-mono">{row.shortName}</span>
            )}
          </div>
        ),
        sortable: true,
        sortValue: (row) => row.legalName.toLowerCase(),
      },
      {
        key: 'taxId',
        header: t('admin.tenants.detail.companies.columns.taxId'),
        accessor: (row) =>
          row.taxId ? (
            <span className="font-mono text-xs">{row.taxId}</span>
          ) : (
            <span className="text-text-muted">—</span>
          ),
        sortable: true,
        sortValue: (row) => row.taxId ?? '',
      },
      {
        key: 'usersCount',
        header: t('admin.tenants.detail.companies.columns.usersCount'),
        accessor: (row) => <span>{row.usersCount}</span>,
        sortable: true,
        sortValue: (row) => row.usersCount,
        className: 'text-right',
        headerClassName: 'text-right',
      },
      {
        key: 'status',
        header: t('admin.tenants.detail.companies.columns.status'),
        accessor: (row) => <Badge color={statusColor(row)}>{statusLabel(row)}</Badge>,
      },
      {
        key: 'primary',
        header: t('admin.tenants.detail.companies.columns.primary'),
        accessor: (row) =>
          row.isPrimary ? (
            <Badge color="blue">{t('admin.tenants.detail.companies.primary.yes')}</Badge>
          ) : (
            <span className="text-text-muted">
              {t('admin.tenants.detail.companies.primary.no')}
            </span>
          ),
      },
      {
        key: 'createdAt',
        header: t('admin.tenants.detail.companies.columns.createdAt'),
        accessor: (row) => (
          <span className="text-xs text-text-secondary">{formatDate(row.createdAt)}</span>
        ),
      },
      {
        key: 'actions',
        header: t('admin.tenants.detail.companies.columns.actions'),
        accessor: (row) =>
          row.gcrecord ? (
            <span className="text-text-muted">—</span>
          ) : (
            <div className="flex items-center justify-end gap-1">
              <Button
                variant="secondary"
                size="sm"
                data-testid={`tenant-company-edit-${row.id}`}
                onClick={() => openEdit(row)}
              >
                {t('common.edit')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                disabled={row.isPrimary}
                title={
                  row.isPrimary
                    ? t('admin.tenants.detail.companies.remove.primaryBlocked')
                    : undefined
                }
                data-testid={`tenant-company-delete-${row.id}`}
                onClick={() => setPendingDelete(row)}
              >
                {t('common.delete')}
              </Button>
            </div>
          ),
        className: 'text-right',
        headerClassName: 'text-right',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} height={48} />
        ))}
      </div>
    );
  }

  if (query.isError) {
    return (
      <div
        role="alert"
        className="rounded-xl border border-red-300 bg-red-50 p-6 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
      >
        <p>{t('admin.tenants.detail.companies.loadError')}</p>
        <Button variant="secondary" size="sm" className="mt-3" onClick={() => void query.refetch()}>
          {t('admin.tenants.detail.companies.retry')}
        </Button>
      </div>
    );
  }

  const data = query.data;

  if (data?.dbUnavailable) {
    return (
      <div
        role="status"
        className="rounded-xl border border-amber-300 bg-amber-50 p-6 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200"
      >
        {data?.dbUnavailableReason === 'TENANT_UPDATING'
          ? t('admin.tenants.dbUpdatingNotice')
          : t('admin.tenants.detail.companies.dbUnavailable')}
      </div>
    );
  }

  const rows = data?.companies ?? [];
  const emptyMessage =
    searchDebounced.length > 0
      ? t('admin.tenants.detail.companies.emptySearch')
      : t('admin.tenants.detail.companies.empty');

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">
            {t('admin.tenants.detail.companies.title')}
          </h3>
          <p className="mt-0.5 text-xs text-text-tertiary">
            {t('admin.tenants.detail.companies.subtitle')}
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          data-testid="tenant-companies-add-btn"
          onClick={openCreate}
        >
          {t('admin.tenants.detail.companies.add.button')}
        </Button>
      </div>

      <div className="max-w-sm">
        <Input
          type="search"
          placeholder={t('admin.tenants.detail.companies.searchPlaceholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label={t('admin.tenants.detail.companies.searchPlaceholder')}
        />
      </div>

      <Table<CompanyRow>
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        pageSize={20}
        emptyState={<p className="text-sm text-text-muted">{emptyMessage}</p>}
      />

      <Modal
        open={addOpen}
        onClose={() => (saveMutation.isPending ? undefined : setAddOpen(false))}
        title={
          editingId
            ? t('admin.tenants.detail.companies.add.editTitle')
            : t('admin.tenants.detail.companies.add.title')
        }
        description={t('admin.tenants.detail.companies.add.subtitle')}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setAddOpen(false)}
              disabled={saveMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              data-testid="tenant-companies-add-submit"
              disabled={!addForm.legalName.trim() || saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              {editingId ? t('common.save') : t('admin.tenants.detail.companies.add.submit')}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <Input
            label={t('admin.tenants.detail.companies.add.legalName')}
            required
            value={addForm.legalName}
            onChange={(e) => setAddForm((f) => ({ ...f, legalName: e.target.value }))}
            data-testid="tenant-companies-add-legalName"
          />
          <Input
            label={t('admin.tenants.detail.companies.add.shortName')}
            value={addForm.shortName}
            onChange={(e) => setAddForm((f) => ({ ...f, shortName: e.target.value }))}
            data-testid="tenant-companies-add-shortName"
          />
          <Input
            label={t('admin.tenants.detail.companies.add.taxId')}
            value={addForm.taxId}
            error={taxIdError ?? undefined}
            onChange={(e) => {
              if (taxIdError) setTaxIdError(null);
              setAddForm((f) => ({ ...f, taxId: e.target.value }));
            }}
            data-testid="tenant-companies-add-taxId"
          />
        </div>
      </Modal>

      <ConfirmDialog
        open={pendingDelete !== null}
        variant="danger"
        title={t('admin.tenants.detail.companies.remove.confirmTitle')}
        message={
          pendingDelete
            ? t('admin.tenants.detail.companies.remove.confirmBody', {
                name: pendingDelete.legalName,
              })
            : ''
        }
        confirmLabel={t('common.delete')}
        confirming={deleteMutation.isPending}
        onConfirm={() => pendingDelete && deleteMutation.mutate(pendingDelete.id)}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}
